import React from "react"
import { AxiosError } from "axios"
import * as Yup from "yup"
import { Form, Formik, FormikHelpers } from "formik"
import { toast } from "react-toastify"
import { IHttpErrorResponseModel, IPersonnelDdcModel } from "@common-models/*"
import {
     CONST_ENTITE_CODE_GS,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_MIME_APPLICATION_PDF,
     CONST_MIME_IMAGE_JPEG,
     CONST_MIME_IMAGE_JPG,
     CONST_MIME_IMAGE_PNG,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_ANNONCE_HANDICAP_OU_MALADIE_GRAVE_ENFANT,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_AUTRE,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_CONGES_MATERNITE,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_CONGES_PATERNITE_MANY_ENFANT,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_CONGES_PATERNITE_UN_ENFANT,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_BEAU_PERE_BELLE_MERE_DU_CONJOINT_MARIE_PACS,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_CONJOINT_CONCUBUN_PARTENAIRE_PACS,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_ENFANT_MOINS_25_ANS,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_ENFANT_PLUS_25_ANS,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_FRERE_SOEUR,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_GRAND_PARENT_OU_ARRIERE_GRAND_PARENT,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_PARENT,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_PARENTS_CONJOINT_LIE_PAR_MARIAGE,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_MARIAGE,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_MARIAGE_ENFANT,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_NAISSANCE_ENFANT,
     CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_PACS,
     CONST_PERSONNEL_DDC_SOUS_TYPES_FOR_SELECT2,
     CONST_PERSONNEL_DDC_TYPE_ABSENCE_EXCEPTIONNELLE,
     CONST_PERSONNEL_DDC_TYPE_ABSENCE_RTT,
     CONST_PERSONNEL_DDC_TYPES_FOR_SELECT2,
} from "@common-constants/*"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyAlert from "@common-utils/MyAlert"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"
import { ICreateConnectedUserDdcRequestModel } from "../core/_models"
import { createConnectedUserDdcRequest } from "../core/_requests"
import { useAuth } from "../../../../../AppContext"
import MySelectField from "@common-utils/fields/MySelectField"
import moment from "moment"
import MyFileField from "@common-utils/fields/MyFileField"
import MyTextareaField from "@common-utils/fields/MyTextareaField"
import MyCheckBoxField from "@common-utils/fields/MyCheckBoxField"
import MySelectCreatableField from "@common-utils/fields/MySelectCreatableField"

const ConnectedPersonnelCreateDdcForm = ({ duOrAuWithThisMonthAndYear, du, au, callback = () => {} }: IConnectedUserCreateDdcFormPropsModel) => {
     const { currentUser } = useAuth()

     // Schema
     const schema = Yup.object().shape({
          du: Yup.string().label(`"Date de début"`).required(),
          au: Yup.string().label(`"Date de fin"`).required(),
          isDemiJournee: Yup.boolean().label(`"Demi journée"`).required(),
          demiJournees: Yup.array()
               .label(`"Demi journées"`)
               .when(["du", "au", "isDemiJournee"], {
                    is: (du: string, au: string, isDemiJournee: boolean) => {
                         const diffDays = moment(au).diff(moment(du), "days")
                         return isDemiJournee && diffDays >= 1
                    },
                    then: schema => schema.min(1).required(),
                    otherwise: schema => schema.notRequired(),
               }),
          type: Yup.string().label(`"Type"`).required(),
          sousType: Yup.string()
               .label(`"Sous type"`)
               .when("type", {
                    is: CONST_PERSONNEL_DDC_TYPE_ABSENCE_EXCEPTIONNELLE,
                    then: schema => schema.required(),
               }),
          motif: Yup.string()
               .label(`"Motif"`)
               .when("type", {
                    is: CONST_PERSONNEL_DDC_TYPE_ABSENCE_EXCEPTIONNELLE,
                    then: schema => schema.required(),
               }),
          justificatif: Yup.mixed()
               .nullable()
               .test("fileFormat", "Seuls les formats JPEG, PNG ou PDF sont acceptés", (value: any) => {
                    if (!value) return true // If no file is provided, skip the test
                    return [CONST_MIME_IMAGE_PNG, CONST_MIME_IMAGE_JPEG, CONST_MIME_IMAGE_JPG, CONST_MIME_APPLICATION_PDF].includes(value.type)
               })
               .test("fileSize", "Le document ne doit pas dépasser le 10MB", (value: any) => {
                    if (!value) return true // If no file is provided, skip the test
                    return value.size <= 10 * 1024 * 1024
               })
               .label(`"Justificatif"`),
     })

     // Init values
     const initialValues: ICreateConnectedUserDdcRequestModel = {
          isDemiJournee: false,
     }

     // Handle submit
     function handleSubmit(values: ICreateConnectedUserDdcRequestModel, helpers: FormikHelpers<ICreateConnectedUserDdcRequestModel>) {
          helpers.setStatus(null)

          createConnectedUserDdcRequest(values)
               .then(r => {
                    callback(r.data)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    // Set form errors
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }

                    // Set form global status and notify user using a toast
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })

                    // Stop submit loader
                    helpers.setSubmitting(false)
               })
     }

     // Alert info absence ixciptionnil
     const InfoAccordingToSousCategorieAbsenceExceptionnelle = ({ sousType, gender }: { sousType: string; gender: string }) => {
          let text: string
          switch (sousType) {
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_MARIAGE:
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_PACS:
                    text = "4 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_MARIAGE_ENFANT:
                    text = "1 jour maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_NAISSANCE_ENFANT:
                    text = "3 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_CONGES_PATERNITE_UN_ENFANT:
                    text = "25 jours maximum (hors naissance enfant). Si fractionnement, merci de vous renseigner auprès du service administratif."
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_CONGES_PATERNITE_MANY_ENFANT:
                    text = "32 jours maximum (hors naissance enfant). Si fractionnement, merci de vous renseigner auprès du service administratif."
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_ENFANT_MOINS_25_ANS:
                    text = "22 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_ENFANT_PLUS_25_ANS:
                    text = "5 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_CONJOINT_CONCUBUN_PARTENAIRE_PACS:
                    text = "3 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_PARENT:
                    text = "3 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_PARENTS_CONJOINT_LIE_PAR_MARIAGE:
                    text = "3 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_FRERE_SOEUR:
                    text = "3 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_BEAU_PERE_BELLE_MERE_DU_CONJOINT_MARIE_PACS:
                    text = "3 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_ANNONCE_HANDICAP_OU_MALADIE_GRAVE_ENFANT:
                    text = "5 jours maximum"
                    break
               case CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_DECES_GRAND_PARENT_OU_ARRIERE_GRAND_PARENT:
                    text = "2 jours maximum"
                    break
               default:
                    text = ""
          }

          return (
               <MyAlert type={"info"}>
                    <span>Vous avez le droit à {text}</span>
               </MyAlert>
          )
     }

     return (
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
               {helpers => {
                    console.log(helpers.values)
                    return (
                         <Form noValidate autoComplete="off">
                              {helpers.status && (
                                   <MyAlert type={"danger"} classNames={"mb-4"}>
                                        {helpers.status}
                                   </MyAlert>
                              )}

                              {helpers.values.type === CONST_PERSONNEL_DDC_TYPE_ABSENCE_EXCEPTIONNELLE &&
                                   helpers.values.sousType &&
                                   helpers.values.sousType !== CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_AUTRE &&
                                   helpers.values.sousType !== CONST_PERSONNEL_DDC_SOUS_TYPE_ABSENCE_EXCEPTIONNELLE_CONGES_MATERNITE && (
                                        <div className="mb-4">
                                             <InfoAccordingToSousCategorieAbsenceExceptionnelle sousType={helpers.values.sousType} gender={currentUser!.civilite as string} />
                                        </div>
                                   )}
                              <div className="row mb-4">
                                   <label className="col-lg-4 col-form-label fw-bold fs-6 required">Précisez la catégorie</label>

                                   <div className="col-lg-8">
                                        <MySelectField
                                             options={CONST_PERSONNEL_DDC_TYPES_FOR_SELECT2.filter(
                                                  item =>
                                                       item.value !== CONST_PERSONNEL_DDC_TYPE_ABSENCE_RTT ||
                                                       (item.value === CONST_PERSONNEL_DDC_TYPE_ABSENCE_RTT && currentUser!.mainEntite?.code === CONST_ENTITE_CODE_GS)
                                             )}
                                             name={`type`}
                                             isSearchable={false}
                                             value={helpers.values.type}
                                             onChange={value => helpers.setFieldValue(`type`, value)}
                                             isInvalid={!!helpers.errors.type}
                                        />
                                        {helpers.errors.type && <div className={"text-danger"}>{helpers.errors.type}</div>}
                                   </div>
                              </div>

                              {helpers.values.type === CONST_PERSONNEL_DDC_TYPE_ABSENCE_EXCEPTIONNELLE && (
                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Précisez la sous catégorie</label>

                                        <div className="col-lg-8">
                                             <MySelectField
                                                  options={CONST_PERSONNEL_DDC_SOUS_TYPES_FOR_SELECT2}
                                                  name={`sousType`}
                                                  isSearchable={false}
                                                  value={helpers.values.sousType}
                                                  onChange={value => helpers.setFieldValue(`sousType`, value)}
                                                  isInvalid={!!helpers.errors.sousType}
                                             />
                                             {helpers.errors.sousType && <div className={"text-danger"}>{helpers.errors.sousType}</div>}
                                        </div>
                                   </div>
                              )}

                              <div className="row mb-4">
                                   <label className="col-lg-4 col-form-label fw-bold fs-6 required">Saisissez la période</label>

                                   <div className="col-lg-8">
                                        <div className="row">
                                             <div className="col">
                                                  <MyDateDayMonthYearField
                                                       onChange={date => helpers.setFieldValue("du", date)}
                                                       minDate={
                                                            duOrAuWithThisMonthAndYear
                                                                 ? moment(duOrAuWithThisMonthAndYear)?.startOf("month").format("YYYY-MM-DD")
                                                                 : du && moment(du)?.startOf("month").format("YYYY-MM-DD")
                                                       }
                                                       maxDate={
                                                            duOrAuWithThisMonthAndYear
                                                                 ? moment(duOrAuWithThisMonthAndYear)?.endOf("month").format("YYYY-MM-DD")
                                                                 : au && moment(au)?.endOf("month").format("YYYY-MM-DD")
                                                       }
                                                       value={helpers.values.du}
                                                       name={"du"}
                                                       placeholder={"Du (inclus)"}
                                                       isInvalid={!!helpers.errors.du}
                                                  />
                                                  {helpers.errors.du && <div className={"text-danger"}>{helpers.errors.du}</div>}
                                             </div>
                                             <div className="col">
                                                  <MyDateDayMonthYearField
                                                       onChange={date => helpers.setFieldValue("au", date)}
                                                       value={helpers.values.au}
                                                       minDate={helpers.values.du}
                                                       maxDate={
                                                            duOrAuWithThisMonthAndYear
                                                                 ? moment(duOrAuWithThisMonthAndYear)?.endOf("month").format("YYYY-MM-DD")
                                                                 : au && moment(au)?.endOf("month").format("YYYY-MM-DD")
                                                       }
                                                       name={"au"}
                                                       placeholder={"Au (inclus)"}
                                                       isInvalid={!!helpers.errors.au}
                                                  />
                                                  {helpers.errors.au && <div className={"text-danger"}>{helpers.errors.au}</div>}
                                             </div>
                                        </div>
                                   </div>
                              </div>

                              {helpers.values.du && helpers.values.au && (
                                   <div className={"mb-4"}>
                                        <div className="row">
                                             <label className="col-lg-4 col-form-label fw-bold fs-6">
                                                  {moment(helpers.values.au).diff(moment(helpers.values.du), "days") >= 1
                                                       ? "Y a-t-il des demi-journées ?"
                                                       : "Est-ce une demi-journée ?"}
                                             </label>

                                             <div className="col-lg-8 d-flex align-items-center">
                                                  <MyCheckBoxField
                                                       name={"isDemiJournee"}
                                                       value={helpers.values.isDemiJournee}
                                                       isInvalid={!!helpers.errors.isDemiJournee}
                                                       onChange={val => helpers.setFieldValue("isDemiJournee", val)}
                                                  />
                                                  {helpers.errors.isDemiJournee && <div className={"text-danger"}>{helpers.errors.isDemiJournee}</div>}
                                             </div>
                                        </div>

                                        {helpers.values.isDemiJournee && moment(helpers.values.au).diff(moment(helpers.values.du), "days") >= 1 && (
                                             <>
                                                  <MySelectCreatableField
                                                       name={"demiJournees"}
                                                       options={[]}
                                                       value={helpers.values.demiJournees}
                                                       onChange={val => helpers.setFieldValue("demiJournees", val)}
                                                       isInvalid={!!helpers.errors.demiJournees}
                                                       placeholder={"Saisissez les demi journées au format jj/mm/aaaa"}
                                                       isValidNewOption={val => {
                                                            const isValidDateFormat = moment(val, "DD/MM/YYYY", true).isValid()
                                                            const isWithinDateRange = moment(val, "DD/MM/YYYY").isBetween(
                                                                 moment(helpers.values.du),
                                                                 moment(helpers.values.au),
                                                                 undefined,
                                                                 "[]"
                                                            )

                                                            return isValidDateFormat && isWithinDateRange
                                                       }}
                                                       classNames={"mt-4"}
                                                  />
                                                  {helpers.errors.demiJournees && <div className={"text-danger"}>{helpers.errors.demiJournees}</div>}
                                             </>
                                        )}
                                   </div>
                              )}

                              {helpers.values.type === CONST_PERSONNEL_DDC_TYPE_ABSENCE_EXCEPTIONNELLE && (
                                   <>
                                        <div className="row mb-4">
                                             <label className="col-lg-4 col-form-label fw-bold fs-6 required">Précisez le motif</label>

                                             <div className="col-lg-8">
                                                  <MyTextareaField
                                                       name={"motif"}
                                                       value={helpers.values.motif}
                                                       isInvalid={!!helpers.errors.motif}
                                                       onChange={val => helpers.setFieldValue("motif", val)}
                                                  />
                                                  {helpers.errors.motif && <div className={"text-danger"}>{helpers.errors.motif}</div>}
                                             </div>
                                        </div>
                                        <div className="row mb-4">
                                             <label className="col-lg-4 col-form-label fw-bold fs-6">
                                                  Justificatif <i>(optionnel)</i>
                                             </label>

                                             <div className="col-lg-8">
                                                  <MyFileField
                                                       name={"justificatif"}
                                                       isInvalid={!!helpers.errors.justificatif}
                                                       onChange={value => helpers.setFieldValue("justificatif", value)}
                                                  />
                                                  {helpers.errors.justificatif && <div className={"text-danger"}>{helpers.errors.justificatif}</div>}
                                             </div>
                                        </div>
                                   </>
                              )}

                              {/* Submit button */}
                              <div className={"d-flex justify-content-end"}>
                                   <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={helpers.isSubmitting}>
                                        {!helpers.isSubmitting ? <>Envoyer</> : <MySimpleSpinner size={"sm"} />}
                                   </button>
                              </div>
                         </Form>
                    )
               }}
          </Formik>
     )
}

interface IConnectedUserCreateDdcFormPropsModel {
     callback?: (ddc: IPersonnelDdcModel) => void
     duOrAuWithThisMonthAndYear?: string
     du?: string
     au?: string
}

export default ConnectedPersonnelCreateDdcForm
